// src/components/InvestigationModal.js
// Author: Sunil Jain
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, Grid,
    CircularProgress, Paper, Divider, Stack, Chip, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, useTheme, Tooltip, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';

import AssistantWidget from './AssistantWidget';
import { useAriaContext } from '../contexts/AriaContext';
import ReadOnlySurveyRenderer from './ReadOnlySurveyRenderer';

// [CONFIG] UPDATED: Matches the path used in the Patient Portal (SymptomsLogForm.js)
const BODY_MAP_CANDIDATES = [
    "/images/body/anterior_view.svg", // <--- Exact path from Patient Portal
    "/assets/body_map.png",
    "/assets/body_map.svg",
    "/body_map.png",
    "/body_map.svg"
];

// --- Helper: Robust JSON Parser ---
// Handles cases where backend logs might be prefixed with "log count..." or other noise
const parsePotentialJson = (str) => {
    if (typeof str !== 'string') return null;
    try {
        return JSON.parse(str);
    } catch (e) {
        // Attempt to extract JSON substring if there's garbage prefix/suffix
        const firstOpen = str.indexOf('{');
        const lastClose = str.lastIndexOf('}');
        if (firstOpen !== -1 && lastClose > firstOpen) {
            try {
                const cleaned = str.substring(firstOpen, lastClose + 1);
                return JSON.parse(cleaned);
            } catch (e2) {
                return null;
            }
        }
        return null;
    }
};

// --- Self-Healing Pain Map Viewer ---
const PainMapViewer = ({ points = [] }) => {
    const [imgSrc, setImgSrc] = useState(process.env.PUBLIC_URL + BODY_MAP_CANDIDATES[0]);
    const [candidateIndex, setCandidateIndex] = useState(0);
    const [hasError, setHasError] = useState(false);

    const handleImageError = () => {
        const nextIndex = candidateIndex + 1;
        if (nextIndex < BODY_MAP_CANDIDATES.length) {
            console.log(`Body map failed at ${imgSrc}, trying: ${BODY_MAP_CANDIDATES[nextIndex]}`);
            setCandidateIndex(nextIndex);
            setImgSrc(process.env.PUBLIC_URL + BODY_MAP_CANDIDATES[nextIndex]);
        } else {
            setHasError(true);
        }
    };

    if (!points || points.length === 0) return null;

    return (
        <Box sx={{ mb: 4, mt: 1, textAlign: 'center' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                PATIENT REPORTED PAIN MAP
            </Typography>
            
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '320px',
                    margin: '0 auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 3,
                    bgcolor: '#fff',
                    minHeight: '400px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
            >
                {!hasError ? (
                    <img 
                        src={imgSrc} 
                        alt="Body Map" 
                        style={{ display: 'block', width: '100%', height: 'auto' }} 
                        onError={handleImageError}
                    />
                ) : (
                    <Box sx={{ 
                        width: '100%', height: '450px', 
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        bgcolor: '#f8f9fa', color: 'text.secondary', p: 3,
                        border: '2px dashed #ccc'
                    }}>
                        <Typography variant="h6" color="error" gutterBottom>Map Image Missing</Typography>
                        <Typography variant="caption" align="center">
                            Please ensure <b>/images/body/anterior_view.svg</b> exists in the Clinician Portal's public folder.
                        </Typography>
                    </Box>
                )}
                
                {points.map((point, index) => (
                    <Tooltip 
                        key={point.id || index} 
                        title={
                            <Box sx={{ p: 0.5, textAlign: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ffecb3' }}>
                                    {point.label || 'Pain Point'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                    Intensity: {point.intensity || '?'}/10
                                </Typography>
                            </Box>
                        } 
                        arrow
                        placement="top"
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                left: `${point.x}%`,
                                top: `${point.y}%`,
                                width: 24,
                                height: 24,
                                bgcolor: (point.intensity >= 8) ? 'error.main' : ((point.intensity >= 5) ? 'warning.main' : 'warning.light'),
                                borderRadius: '50%',
                                border: '3px solid white',
                                boxShadow: '0 3px 8px rgba(0,0,0,0.4)',
                                transform: 'translate(-50%, -50%)',
                                cursor: 'pointer',
                                zIndex: 10,
                                transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                '&:hover': { 
                                    transform: 'translate(-50%, -50%) scale(1.4)',
                                    zIndex: 20,
                                    boxShadow: '0 6px 12px rgba(0,0,0,0.5)'
                                },
                                animation: (point.intensity >= 8) ? 'pulse 2s infinite' : 'none',
                                '@keyframes pulse': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.7)' },
                                    '70%': { boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' }
                                }
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};

// --- Detail View for Medications ---
const MedicationDetailView = ({ medData }) => {
    if (!medData) {
        return <Paper variant="outlined" sx={{ p: 2, height: '100%' }}><Typography>Medication data not available.</Typography></Paper>;
    }

    const { item_name_detailed, status, adherence } = medData;

    return (
        <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6">{item_name_detailed}</Typography>
                <Chip label={status?.replace(/_/g, ' ') || 'UNKNOWN'} color={status === 'active' ? 'success' : 'default'} sx={{ textTransform: 'capitalize', mt: 1 }} />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Adherence Log</Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                        <TableHead><TableRow><TableCell>Date</TableCell><TableCell align="center">Required</TableCell><TableCell align="center">Taken</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                        <TableBody>
                            {adherence?.slice().reverse().map((day) => (
                                <TableRow key={day.date} hover>
                                    <TableCell>{format(parseISO(day.date), 'EEEE, MMM d, yyyy')}</TableCell>
                                    <TableCell align="center">{day.doses_required}</TableCell>
                                    <TableCell align="center">{day.doses_taken}</TableCell>
                                    <TableCell><Chip label={day.status.replace(/_/g, ' ')} size="small" variant="outlined" color={day.status === 'taken' ? 'success' : (day.status === 'missed' ? 'error' : 'default')} sx={{ textTransform: 'capitalize' }} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Stack>
    );
};

// --- Detail View for Vitals ---
const VITAL_METADATA = { bp_systolic: { label: 'Systolic BP', color: '#d32f2f', unit: 'mmHg' }, bp_diastolic: { label: 'Diastolic BP', color: '#d32f2f', unit: 'mmHg' }, heart_rate: { label: 'Heart Rate', color: '#f57c00', unit: 'bpm' }, weight_kg: { label: 'Weight', color: '#1976d2', unit: 'kg' }, oxygen_saturation: { label: 'SpO2', color: '#388e3c', unit: '%' }, temperature_celsius: { label: 'Temp', color: '#7b1fa2', unit: '°C' } };
const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (<Paper elevation={3} sx={{ p: 1.5 }}><Typography variant="caption" display="block">{`Date: ${format(new Date(label), 'MMM d, yyyy HH:mm')}`}</Typography><Typography variant="body2" sx={{ fontWeight: 'bold', color: payload[0].stroke }}>{`${payload[0].value} ${payload[0].payload.unit}`}</Typography></Paper>);
    } return null;
};
const VitalsDetailView = ({ vitalData }) => {
    const theme = useTheme();
    if (!vitalData || !vitalData.series || vitalData.series.length === 0) { return <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}><Typography>No detailed vitals data available.</Typography></Paper>; }
    const metadata = VITAL_METADATA[vitalData.parameter_name] || { label: vitalData.parameter_name, color: theme.palette.primary.main, unit: '' };
    const chartData = vitalData.series.map(p => ({ timestamp: new Date(p.timestamp).getTime(), value: p.value, unit: metadata.unit })).sort((a, b) => a.timestamp - b.timestamp);
    return (
        <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%"><LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="timestamp" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(unixTime) => format(new Date(unixTime), 'MMM d')} /><YAxis domain={['auto', 'auto']} label={{ value: metadata.unit, angle: -90, position: 'insideLeft' }} /><RechartsTooltip content={<CustomChartTooltip />} /><Line type="monotone" dataKey="value" stroke={metadata.color} strokeWidth={2} dot={{ r: 3 }} /></LineChart></ResponsiveContainer>
                </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>All Readings</Typography>
                <TableContainer sx={{ maxHeight: 300 }}><Table stickyHeader size="small"><TableHead><TableRow><TableCell>Date & Time</TableCell><TableCell align="right">Value ({metadata.unit})</TableCell></TableRow></TableHead><TableBody>{chartData.slice().reverse().map((row) => (<TableRow key={row.timestamp}><TableCell>{format(new Date(row.timestamp), 'MMM d, yyyy, p')}</TableCell><TableCell align="right">{row.value}</TableCell></TableRow>))}</TableBody></Table></TableContainer>
            </Paper>
        </Stack>
    );
};

// --- Smart Object Renderer ---
const RenderObjectData = ({ data }) => {
    if (!data || typeof data !== 'object') return null;

    // Clone to manipulate display
    const displayData = { ...data };

    // [FIX] Format Duration nicely
    if ('duration_hours' in displayData || 'duration_minutes' in displayData) {
        const hrs = parseInt(displayData.duration_hours || 0, 10);
        const mins = parseInt(displayData.duration_minutes || 0, 10);
        
        let durationStr = '';
        if (hrs > 0) durationStr += `${hrs} hr `;
        if (mins > 0 || hrs === 0) durationStr += `${mins} min`;
        
        displayData['duration'] = durationStr.trim();
        
        // Remove raw fields
        delete displayData.duration_hours;
        delete displayData.duration_minutes;
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
                <TableBody>
                    {Object.entries(displayData).map(([key, value]) => {
                        // [FIX] Skip internal metadata fields to reduce noise
                        if (['status', 'log_date', 'patient_regimen_id', 'episode_of_care_id', 'item_name', 'logged_by', 'points_earned', 'scheduled_time', 'log_count'].includes(key)) return null;
                        
                        let displayValue = value;
                        if (typeof value === 'boolean') displayValue = value ? 'Yes' : 'No';
                        else if (key === 'pain_points' && Array.isArray(value)) {
                            // Text summary of pain points
                            displayValue = value.map((pt, i) => (
                                <div key={i}>
                                    • <strong>{pt.label || 'Point'}</strong>: {pt.intensity}/10
                                </div>
                            ));
                        }
                        else if (typeof value === 'object' && value !== null) displayValue = JSON.stringify(value);
                        
                        return (
                            <TableRow key={key}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%', textTransform:'capitalize', color: 'text.secondary' }}>
                                    {key.replace(/_/g, ' ')}
                                </TableCell>
                                <TableCell>{displayValue}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// --- Updated Default/Summary Detail View ---
const DefaultDetailView = ({ taskData }) => {
    // 1. Structured Survey/Form Responses
    if (taskData && taskData.content_item && taskData.adherence_logs) {
        const lastSubmission = taskData.adherence_logs?.filter(log => log.event_type === 'response_submitted')?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        const surveyProps = { surveyTitle: taskData.content_item.title || 'Survey Response', blocks: taskData.content_item.processed_content || [], patientResponses: lastSubmission?.details?.response_data || {} };
        return <ReadOnlySurveyRenderer {...surveyProps} />;
    }

    // 2. Daily Logs (Symptoms, Diet, Journal)
    if (taskData && taskData.summary_text) {
        // [FIX] Use robust parser to handle polluted strings
        const parsedData = parsePotentialJson(taskData.summary_text);
        const isJson = parsedData !== null;

        return (
            <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{taskData.item_name}</Typography>
                        <Chip label={taskData.submitted_at ? format(new Date(taskData.submitted_at), 'MMM d, p') : 'No Date'} size="small" />
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    
                    {isJson ? (
                        <>
                            {/* Smartly detect and render the visual Pain Map */}
                            {parsedData.pain_points && Array.isArray(parsedData.pain_points) && (
                                <PainMapViewer points={parsedData.pain_points} />
                            )}
                            
                            <RenderObjectData data={parsedData} />
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontStyle: taskData.item_name === 'Journal Entry' ? 'italic' : 'normal' }}>
                            {taskData.summary_text}
                        </Typography>
                    )}
                </Paper>

                {taskData.details && Object.keys(taskData.details).length > 0 && (
                     <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">Additional Log Details</Typography>
                        <RenderObjectData data={taskData.details} />
                     </Paper>
                )}
            </Stack>
        );
    }

    // 4. Fallback for Raw Data
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary" gutterBottom>Raw Data View</Typography>
            <pre style={{whiteSpace:'pre-wrap', wordBreak:'break-all', fontSize:'0.75rem', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px'}}>
                {JSON.stringify(taskData, null, 2)}
            </pre>
        </Paper>
    );
};

export default function InvestigationModal({ open, onClose, taskType, taskId, title, patientId, episodeId, taskData, data, task }) {
    const { deepPatientContext, isContextLoading } = useAriaContext();

    // Determine effective data source
    const effectiveData = taskData || data || task;

    if (!effectiveData) {
        return null; 
    }

    const renderDetailContent = () => {
        switch (taskType) {
            case 'medication': return <MedicationDetailView medData={effectiveData} />;
            case 'vitals': return <VitalsDetailView vitalData={effectiveData} />;
            default: return <DefaultDetailView taskData={effectiveData} />;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { borderRadius: { sm: 2 } } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 500 }}>Investigation: {title || effectiveData.item_name || 'Details'}</Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'grey.100', p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden' }}>
                <Grid container sx={{ height: '100%' }}>
                    <Grid item xs={12} md={7} sx={{ p: 2, overflowY: 'auto', height: '100%' }}>{renderDetailContent()}</Grid>
                    <Grid item xs={12} md={5} sx={{ borderLeft: { md: '1px solid' }, borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {isContextLoading ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading AI Context...</Typography></Box>
                        ) : (
                            <AssistantWidget patientId={patientId} episodeId={episodeId} contextType={taskType} initialMessage={`Analyze ${title || effectiveData.item_name}`} activeDeepContext={deepPatientContext} externallyControlledOpen={open} />
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}